import React, { useEffect, useState } from 'react';
import MAvatar from '@/components/ui/atoms/MAvatar';
import MBadge from '@/components/ui/atoms/MBadge';
import MDialog from '@/components/ui/atoms/MDialog';
import MaatraLogo from '@/components/ui/atoms/MaatraLogo';
import { IconUserStar } from '@tabler/icons-react';
import { useFamily } from '@/hooks/useFamily';
import { useFamilyStats } from '@/hooks/useFamilyStats';
import { getMemberInfo } from '@/hooks/useMembers';
import { usePendingAccessRequests } from '@/hooks/usePendingAccessRequests';
import { useMemberAccess } from '@/hooks/useMemberAccess';
import { ProfileStrength, MemberStats } from '@/types/familyStats';
import styles from './FamilyWidget.module.css';

const FamilyWidget: React.FC = () => {
  const memberInfo = getMemberInfo(true);
  const loggedInMember = getMemberInfo(true);
  const { data: familyMembers = [], managedIds } = useFamily();
  const { familyStats, fetchFamilyStats } = useFamilyStats({ memberInfo });
  const [memberStatsMap, setMemberStatsMap] = useState<Record<string, MemberStats | null>>({});
  const { pendingRequests, refetch: refetchPendingRequests } = usePendingAccessRequests({
    familyMembers,
    managedIds,
    familyMemberType: memberInfo.family_member_type,
    enabled: true
  });
  const { handleMemberClick: handleMemberClickFromHook, handleRequestAccessWithCleanup, requestingAccess } = useMemberAccess();
  const [showNoAccessDialog, setShowNoAccessDialog] = useState(false);
  const [showConsentDialog, setShowConsentDialog] = useState(false);
  const [selectedMemberForAccess, setSelectedMemberForAccess] = useState<any>(null);

  // Fetch family stats data
  useEffect(() => {
    if (memberInfo?.id) {
      fetchFamilyStats();
    }
  }, [memberInfo?.id, fetchFamilyStats]);

  // Update member stats when family stats data is available
  useEffect(() => {
    if (familyStats?.memberStats) {
      setMemberStatsMap(familyStats.memberStats);
    }
  }, [familyStats]);

  const getProfileStrengthBars = (strength: ProfileStrength): boolean[] => {
    switch (strength) {
      case ProfileStrength.LOW:
        return [true, false, false];
      case ProfileStrength.MODERATE:
        return [true, true, false];
      case ProfileStrength.STRONG:
        return [true, true, true];
      default:
        return [true, false, false];
    }
  };

  const getMemberStats = (memberId: string) => {
    return memberStatsMap?.[memberId];
  };

  const isOwner = (member: any) => {
    return member.family_member_type === 'fo';
  };

  const getMemberDisplayName = (member: any) => {
    const memberId = member.id || member._id;
    const loggedInId = loggedInMember.id || (loggedInMember as any)._id;
    
    if (memberId === loggedInId) {
      return 'You';
    }
    return member.nickname || member.first_name;
  };

  const handleMemberClick = (member: any) => {
    const memberId = member.id || member._id;
    const loggedInId = loggedInMember.id || (loggedInMember as any)._id;
    
    handleMemberClickFromHook({
      memberId,
      loggedInMemberId: loggedInId,
      loggedInMemberType: loggedInMember.family_member_type || '',
      managedIds,
      managedLoading: false, // Add this line
      onNoAccess: () => {
        setSelectedMemberForAccess(member);
        setShowNoAccessDialog(true);
      },
      backUrl: '/dashboard', // Add backUrl for dashboard navigation
    });
  };

  const handleRequestAccess = () => {
    if (!selectedMemberForAccess) return;
    
    setShowNoAccessDialog(false);
    setShowConsentDialog(true);
  };

  const handleConfirmRequestAccess = async () => {
    if (!selectedMemberForAccess) return;
    
    const memberId = loggedInMember.id || (loggedInMember as any)._id;
    const familyMemberId = selectedMemberForAccess.id || selectedMemberForAccess._id;
    const familyId = memberInfo.current_family;
    
    await handleRequestAccessWithCleanup(
      { memberId, familyId, familyMemberId },
      () => {
        setShowConsentDialog(false);
        setSelectedMemberForAccess(null);
        // Refetch pending requests to update the UI
        console.log('FamilyWidget: Calling refetchPendingRequests after successful access request');
        // Add a small delay to allow backend to process the request
        setTimeout(() => {
          console.log('FamilyWidget: Executing delayed refetch');
          refetchPendingRequests();
        }, 1000);
      }
    );
  };

  return (
    <>
    <div className={styles.familyWidget} style={!memberInfo.isExperienced ? {width: '100%', position: 'relative'} : {}} data-family-widget>
      <h2 className={styles.familyTitle}>Your family</h2>
      <p className={styles.familyDescription}>
        Access and manage health information for your family members
      </p>
      
      <div className={styles.membersGrid}>
        {/* Sort members to show logged-in member (You) first, then alphabetically */}
        {[...familyMembers]
          .sort((a, b) => {
            const aId = a.id || a._id;
            const bId = b.id || b._id;
            const loggedInId = loggedInMember.id || (loggedInMember as any)._id;
            
            // Logged-in user always first
            if (aId === loggedInId) return -1;
            if (bId === loggedInId) return 1;
            
            // Sort others alphabetically by first name
            const aName = (a.first_name || '').toLowerCase();
            const bName = (b.first_name || '').toLowerCase();
            return aName.localeCompare(bName);
          })
          .map((member: any) => {
            const memberId = member.id || member._id;
            const memberStats = getMemberStats(memberId);
            const profileStrength = memberStats?.profileStrength || ProfileStrength.LOW;
            const owner = isOwner(member);
            const displayName = getMemberDisplayName(member);
            const hasCheckmark = member.registered_member;
            
            return (
              <button 
                key={memberId} 
                className={`${styles.memberCard}`}
                onClick={() => handleMemberClick(member)}
                type="button"
              >
                <div className={styles.avatarWrapper}>
                  <MAvatar
                    size="lg"
                    type={member.avatar ? 'dp' : 'no-dp'}
                    src={member.avatar}
                    text={`${member.first_name || ''}${member.last_name ? ' ' + member.last_name : ''}`.trim()}
                    textSize={24}
                    badgeIcon={owner ? <IconUserStar size={16} /> : undefined}
                    className={(member.registered_member || owner) ? styles.avatarRegistered : styles.avatarUnregistered}
                  />
                  {hasCheckmark && !owner && (
                    <div className={styles.checkmarkBadge}>
                      <MaatraLogo size={12} fill="#FBFBFB" />
                    </div>
                  )}
                </div>
                {owner && (
                  <MBadge className={styles.ownerBadgeLabel}>Owner</MBadge>
                )}
                <div className={styles.memberName} style={{ marginTop: owner ? '-4px' : '0px' }}>
                  {displayName}
                </div>
                <div className={styles.profileStrengthInline}>
                  <div className={styles.profileStrengthBars}>
                    {getProfileStrengthBars(profileStrength).map((filled, index) => (
                      <div
                        key={`strength-bar-${memberId}-${index}`}
                        className={styles.strengthBar}
                        style={{
                          background: filled
                            ? 'var(--brand-colours-indigo-indigo-300, #8764BB)'
                            : '#EDE8F5)',
                        }}
                      />
                    ))}
                  </div>
                </div>
              </button>
            );
          })}
      </div>
    </div>

    {/* No Access Dialog */}
    <MDialog
      isOpen={showNoAccessDialog}
      onClose={() => {
        setShowNoAccessDialog(false);
        setSelectedMemberForAccess(null);
      }}
      title="No profile access"
      description="You don't have access to this family member's profile. You can ask the Family Owner for access if you need it."
      primaryCtaText={pendingRequests[selectedMemberForAccess?.id || selectedMemberForAccess?._id || ''] ? 'Access Requested' : 'Ask for access'}
      secondaryCtaText="Cancel"
      onPrimaryCta={handleRequestAccess}
      onSecondaryCta={() => {
        setShowNoAccessDialog(false);
        setSelectedMemberForAccess(null);
      }}
      isPrimaryCtaDisabled={pendingRequests[selectedMemberForAccess?.id || selectedMemberForAccess?._id || '']}
    />

    {/* Consent Confirmation Dialog */}
    <MDialog
      isOpen={showConsentDialog}
      onClose={() => {
        setShowConsentDialog(false);
        setSelectedMemberForAccess(null);
      }}
      title="Consent needed"
      description="Please confirm that you have this family member's consent to view their health information."
      primaryCtaText="Confirm"
      secondaryCtaText="Cancel"
      onPrimaryCta={handleConfirmRequestAccess}
      onSecondaryCta={() => {
        setShowConsentDialog(false);
        setSelectedMemberForAccess(null);
      }}
      isPrimaryCtaLoading={requestingAccess}
    />
    </>
  );
};

export default FamilyWidget;
